import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, filter, map, of, take } from 'rxjs';
import { environment } from '@environments/environment';
import { FeatureFlagsResponse } from '../../features/admin/organizations/components/feature-flags/services/feature-flags.service';

@Injectable({
  providedIn: 'root',
})
export class CustomerFeatureFlagsService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/v1/organization/featureflags`;

  public flags = signal<FeatureFlagsResponse | null>(null);
  public isLoading = signal(false);
  private loadedOrgId: number | null = null;

  /** Emits once when flags have been loaded (non-null). */
  public whenReady$: Observable<FeatureFlagsResponse> = toObservable(this.flags).pipe(
    filter((f): f is FeatureFlagsResponse => f !== null),
    take(1),
  );

  /** Clear cached flags so the next loadFlags call fetches fresh data. */
  public invalidateCache(): void {
    this.loadedOrgId = null;
  }

  public loadFlags(organizationId: number): void {
    // Skip reload if flags are already loaded for the same org
    if (this.loadedOrgId === organizationId && this.flags() !== null) {
      return;
    }

    this.isLoading.set(true);
    this.loadedOrgId = organizationId;
    this.flags.set(null);

    this.http
      .get<FeatureFlagsResponse>(`${this.apiUrl}/${organizationId}/`)
      .pipe(
        catchError(() =>
          of({
            organization: organizationId,
            flags: {} as Record<string, boolean>,
            created_at: '',
            updated_at: '',
          }),
        ),
      )
      .subscribe((flags) => {
        this.flags.set(flags);
        this.isLoading.set(false);
      });
  }

  /**
   * Check if a feature flag is enabled for the current org.
   * Returns false for unknown/undefined flags.
   */
  public hasFlag(flag: string): boolean {
    return this.flags()?.flags?.[flag] === true;
  }

  /** Observable that waits for flags to load, then checks a specific flag. */
  public hasFlagAsync(flag: string): Observable<boolean> {
    return this.whenReady$.pipe(map((res) => res.flags?.[flag] === true));
  }
}
